import {
  BrowserTracing,
  captureException,
  init as SentryInit,
  withScope,
} from '@sentry/react';
import {isBetaDomain, isProdEnvironment} from 'utils/environment';
import {getUserData} from 'utils/functions/getUserData/getUserData';

// initialize sentry
SentryInit({
  dsn: 'https://d3c3996777844153b70606ca24e56bab@o222076.ingest.sentry.io/5806608',
  environment: getEnvironment(),
  integrations: [new BrowserTracing()],
  tracesSampleRate: 0.25,
  autoSessionTracking: true,
  ignoreErrors: [
    'handtalk',
    'maze',
    'layers',
    'AuthRefreshError',
    'Token refresh failed',
    'Request call failed',
    'Network Error',
    'Request aborted',
    /No CompositionReview matches the given query/,
    // reCAPTCHA noise
    /reCAPTCHA Timeout/,
    /Missing required parameters: sitekey/,
    // CDN / third-party chunk noise
    /Unexpected token '</,
    // Only ignore known third-party Hand Talk fetch/import failures. Do not blanket-filter generic
    // "Failed to fetch" or "Failed to fetch dynamically imported module" errors because a broken
    // first-party lazy route chunk (for example, a missing /login bundle after deployment) should
    // remain visible in Sentry as an application error, not be silently discarded.
    /Failed to fetch(?: dynamically imported module)?.*(?:handtalk|plugin\.handtalk|initial_MAYA\.json|\/web\/latest\/handtalk)/i,
    // Ably SDK - warnings client side
    /Channel attach timed out/,
    /Connection to server unavailable/,
    /Unable to connect.*fallback hosts/,
    /Token request callback timed out/,
    /Unable to connect \(network unreachable\)/,
  ],
  denyUrls: [/maze/, /handtalk/, /layers/],
  beforeSend: (event) => {
    // drop errors unauthorized and not found
    // TODO: replace this for httpClientIntegration on SDK upgrade to V9
    const httpStatus = event.contexts?.response?.status_code;
    if (httpStatus && httpStatus >= 400 && httpStatus < 500) {
      return null;
    }

    // store sentry event to access it from error boundary page
    try {
      localStorage.setItem(
        'sentryEvent',
        JSON.stringify({event_id: event.event_id}),
      );
    } catch {
      // storage is unavailable: ignore to avoid blocking Sentry
    }

    return enrichSentryEvent(event);
  },
  beforeSendTransaction: (event) => enrichSentryEvent(event),
});

function getEnvironment(): string {
  if (isProdEnvironment) return 'production';
  if (isBetaDomain) return 'beta';
  return 'development';
}

interface SentryEvent {
  tags?: {[key: string]: unknown} | undefined;
}

// intercept and enrich sentry  events
function enrichSentryEvent<Event extends SentryEvent>(event: Event): Event {
  // get user data to enrich sentry event
  const {userRole} = getUserData();

  if (userRole) {
    return {
      ...event,
      tags: {
        ...event?.tags,
        schoolId: userRole?.school,
        schoolName: userRole?.school_name,
        schoolGroupId: userRole?.school_group,
        schoolGradeId: userRole?.school_grade,
        networkId: userRole?.school_network_id,
        schoolGradeName: userRole?.school_grade_name,
        schoolGroupName: userRole?.school_group_name,
      },
    };
  }
  return event;
}

/**
 * Normalize SWR/URLs for grouping in Sentry
 * Ex: /api/user/123/posts?page=1 -> /api/user/:id/posts
 */
export function normalizeApiUrl(key) {
  if (!key) return 'unknown_key';

  // if the key is an array (common in SWR), we take only the first element (URL)
  const url = Array.isArray(key) ? key[0] : key;

  if (typeof url !== 'string') return 'complex_key';

  return url
    .split('?')[0] // 1. Remove Query Params
    .replace(/\/\d+(?=\/|$)/g, '/:id') // 2. Remove numeric IDs between slashes or at the end
    .toLowerCase();
}

// capture API errors
export function captureApiError(error, key) {
  const normalizedKey = normalizeApiUrl(key);

  withScope((scope) => {
    // 1. Identetifies the API endpoint that caused the error
    scope.setTag('api_endpoint', key);

    // 2. If it's an Axios error, extract the request details
    if (error.isAxiosError && error.response) {
      const {status} = error.response;
      const method = error.config?.method?.toUpperCase();

      scope.setTag('status_code', status);
      scope.setTag('method', method);

      // Adds the sent payload and the received response
      scope.setContext('API Details', {
        url: error.config?.url,
        params: error.config?.params,
        request_body: JSON.stringify(error?.config?.data ?? {}, null, 2),
        response_body: JSON.stringify(error?.response?.data ?? {}, null, 2),
      });

      // Creates a unique fingerprint to avoid grouping unrelated errors together
      scope.setFingerprint([
        '{{ default }}',
        String(method),
        String(normalizedKey),
        String(status),
      ]);

      // Define the error level: 5xx is 'error', 4xx is 'warning'
      scope.setLevel(error.response.status >= 500 ? 'error' : 'warning');
    }

    // Since this runs in the SWR flow, the Stack Trace will be much more accurate
    captureException(error);
  });
}
